/**
 * Utility functions for safely handling Builder.io content
 */

/**
 * Checks if a value is a Builder.io internal object
 */
function isBuilderObject(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "@type" in value &&
    typeof (value as { "@type": unknown })["@type"] === "string"
  );
}

/**
 * Safely extracts HTML content from Builder.io richText field
 * Prevents rendering Builder.io internal objects as React children
 */
export function safeBuilderHtml(content: unknown): string {
  // Handle null/undefined
  if (content == null) {
    return "";
  }

  // Handle string content (already HTML)
  if (typeof content === "string") {
    return content;
  }

  // Handle Builder.io richText object
  if (isBuilderObject(content)) {
    const builderObj = content as {
      value?: unknown;
      html?: unknown;
      content?: unknown;
    };

    // Try to extract HTML from common Builder.io richText structures
    if (builderObj.value && typeof builderObj.value === "string") {
      return builderObj.value;
    }

    if (builderObj.html && typeof builderObj.html === "string") {
      return builderObj.html;
    }

    if (builderObj.content && typeof builderObj.content === "string") {
      return builderObj.content;
    }

    // Log warning for debugging
    console.warn("[Builder.io] Unexpected richText structure:", builderObj);
    return "";
  }

  // Handle arrays (shouldn't happen with richText but defensive)
  if (Array.isArray(content)) {
    console.warn("[Builder.io] Array passed as richText content:", content);
    return "";
  }

  // Handle other object types
  if (typeof content === "object") {
    console.warn(
      "[Builder.io] Unknown object passed as richText content:",
      content,
    );
    return "";
  }

  // Convert other primitives to string
  return String(content);
}

/**
 * Safely extracts text content from Builder.io field
 * Handles both string and Builder.io object formats
 */
export function safeBuilderText(content: unknown): string {
  // Handle null/undefined
  if (content == null) {
    return "";
  }

  // Handle string content
  if (typeof content === "string") {
    return content;
  }

  // Handle Builder.io object
  if (isBuilderObject(content)) {
    const builderObj = content as {
      value?: unknown;
      text?: unknown;
      content?: unknown;
    };

    // Try to extract text from common Builder.io text structures
    if (builderObj.value && typeof builderObj.value === "string") {
      return builderObj.value;
    }

    if (builderObj.text && typeof builderObj.text === "string") {
      return builderObj.text;
    }

    if (builderObj.content && typeof builderObj.content === "string") {
      return builderObj.content;
    }

    // Log warning for debugging
    console.warn("[Builder.io] Unexpected text structure:", builderObj);
    return "";
  }

  // Convert other types to string
  return String(content);
}

/**
 * Validates that content is safe to render as React children
 * Throws descriptive error if Builder.io objects are detected
 */
export function validateReactChildren(
  content: unknown,
  componentName: string,
): void {
  if (isBuilderObject(content)) {
    const builderObj = content as {
      "@type"?: string;
      id?: string;
      model?: string;
    };
    throw new Error(
      `[${componentName}] Builder.io object cannot be rendered as React child. ` +
        `Received object with @type: "${builderObj["@type"]}", id: "${builderObj.id || "unknown"}", ` +
        `model: "${builderObj.model || "unknown"}". ` +
        `Use safeBuilderHtml() or safeBuilderText() to extract content safely.`,
    );
  }
}
